// frontend/src/pages/VentaDetail.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBackRounded,
  CalendarMonthOutlined,
  CancelOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  PaymentsOutlined,
  PersonOutlineRounded,
  PictureAsPdfRounded,
  ReceiptLongOutlined,
  TrendingUpRounded,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { cancelVenta, fetchVenta } from '../api/ventas';
import { fetchProductos } from '../api/productos';
import { useLoading } from '../contexts/LoadingContext';
import { generarVentaPDF } from '../utils/pdfHelpers';
import { confirm, toast } from '../utils/alerts';

const formatMoney = (value) => `$ ${Number(value || 0).toFixed(2)}`;

const readable = (value) => {
  const labels = {
    activa: 'Activa',
    cancelada: 'Cancelada',
    cancelado: 'Cancelado',
    finalizada: 'Finalizada',
    pagada: 'Pagada',
    pendiente_pago: 'Pendiente de pago',
    pendiente_envio: 'Pendiente de envío',
    enviado: 'Enviado',
    recibido: 'Recibido',
    entrega: 'Entrega',
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    tarjeta: 'Tarjeta',
    deposito: 'Depósito',
  };
  const key = String(value || '').trim().toLowerCase();
  return labels[key] || String(value || 'Sin definir').replace(/_/g, ' ');
};

const statusColor = (value, type) => {
  const key = String(value || '').toLowerCase();
  if (key.includes('cancel')) return 'error';
  if (type === 'sale') return key === 'finalizada' ? 'success' : 'info';
  if (type === 'payment') return key === 'pagada' ? 'success' : 'warning';
  if (type === 'shipping') {
    if (key === 'recibido') return 'success';
    if (key === 'enviado') return 'info';
    return 'warning';
  }
  return 'default';
};

function DetailValue({ icon: Icon, label, children }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 32,
          height: 32,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'primary.main',
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
          {label}
        </Typography>
        <Box sx={{ fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>{children}</Box>
      </Box>
    </Stack>
  );
}

function AmountLine({ label, value, emphasis, negative }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Typography variant={emphasis ? 'body1' : 'body2'} color={emphasis ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Typography
        variant={emphasis ? 'h6' : 'body2'}
        sx={{
          fontWeight: emphasis ? 800 : 700,
          color: negative ? 'error.main' : (emphasis ? 'success.main' : 'text.primary'),
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function VentaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { start, stop } = useLoading();

  const [venta, setVenta] = useState(null);
  const [productosMap, setProductosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { stop(); }, [stop]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchProductos();
        const arr = Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : []);
        const map = {};
        arr.forEach(p => { map[p.id] = p.nombre; });
        setProductosMap(map);
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const r = await fetchVenta(id);
        const data = r?.data ?? r ?? null;
        setVenta(data);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const formatoFecha = (fechaStr) => new Date(fechaStr).toLocaleString();

  const handleDownloadPdf = () => {
    if (!venta) return;
    generarVentaPDF(venta, productosMap);
  };

  const handleCancelVenta = async () => {
    if (!venta || cancelling) return;
    const accepted = await confirm({
      icon: 'warning',
      title: '¿Cancelar esta venta?',
      text: 'Se liberará la reserva de inventario y la venta quedará registrada como cancelada.',
      confirmText: 'Cancelar venta',
    });
    if (!accepted) return;

    try {
      setCancelling(true);
      const result = await cancelVenta(venta.id);
      setVenta((current) => current ? { ...current, estado_venta: result.estado_venta } : current);
      toast.ok('Venta cancelada', 'El inventario se liberó correctamente.');
    } catch (err) {
      toast.error('No se pudo cancelar', err?.response?.data?.error || err?.message || 'Inténtelo de nuevo.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ minHeight: '45vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Cargando detalle de venta" />
      </Container>
    );
  }
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No fue posible cargar la venta</Typography>
          <Typography color="error">{error}</Typography>
          <Button onClick={() => { start(); nav('/ventas'); }} sx={{ mt: 2 }} variant="contained">
            Volver a ventas
          </Button>
        </Paper>
      </Container>
    );
  }
  if (!venta) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No se encontró la venta.</Typography>
          <Button onClick={() => { start(); nav('/ventas'); }} sx={{ mt: 2 }} variant="contained">
            Volver a ventas
          </Button>
        </Paper>
      </Container>
    );
  }

  const lineas = venta.lineas || [];
  const totalSinDescuento = lineas.reduce(
    (sum, ln) => sum + (Number(ln.cantidad || 0) * Number(ln.precio_unitario || 0)),
    0,
  );
  const descuentoTotal = lineas.reduce((sum, linea) => {
    const bruto = Number(linea.cantidad || 0) * Number(linea.precio_unitario || 0);
    const descuentoLinea =
      linea.descuento_monto != null
        ? Number(linea.descuento_monto || 0)
        : bruto * (Number(linea.descuento || 0) / 100);
    return sum + descuentoLinea;
  }, 0);
  const costoEnvio = Number(venta.costo_envio_proveedor || 0);
  const comision = Number(venta.transportista_comision || 0);
  const totalNeto = Number(
    venta.total_venta_neta || (Number(venta.total_venta || 0) + costoEnvio - comision),
  );
  const gananciaNeta = Number(venta.ganancia_neta || 0);
  const ventaActiva = String(venta.estado_venta || '').toLowerCase() === 'activa';

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 3 }, pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          background: (theme) => `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}0d 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <ReceiptLongOutlined color="primary" />
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                Detalle de venta
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 1.25 }} alignItems={{ sm: 'center' }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                {venta.codigo || `Venta #${venta.id}`}
              </Typography>
              <Typography color="text.secondary">para {venta.cliente_nombre || 'Cliente sin nombre'}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              <Chip size="small" label={readable(venta.estado_venta)} color={statusColor(venta.estado_venta, 'sale')} />
              <Chip size="small" variant="outlined" label={`Pago: ${readable(venta.estado_pago)}`} color={statusColor(venta.estado_pago, 'payment')} />
              <Chip size="small" variant="outlined" label={`Envío: ${readable(venta.estado_envio)}`} color={statusColor(venta.estado_envio, 'shipping')} />
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1}
            alignItems={{ md: 'flex-end' }}
            sx={{ minWidth: { md: 360 } }}
          >
            <Button
              onClick={() => { start(); nav('/ventas'); }}
              variant="outlined"
              startIcon={<ArrowBackRounded />}
              fullWidth
            >
              Volver
            </Button>
            <Button onClick={handleDownloadPdf} variant="contained" startIcon={<PictureAsPdfRounded />} fullWidth>
              Descargar PDF
            </Button>
            {ventaActiva && (
              <Button
                color="error"
                variant="outlined"
                disabled={cancelling}
                onClick={handleCancelVenta}
                startIcon={<CancelOutlined />}
                fullWidth
              >
                {cancelling ? 'Cancelando…' : 'Cancelar'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.25 }}>
              <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid' }}>
                <PersonOutlineRounded fontSize="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Venta y cliente</Typography>
            </Stack>
            <Stack spacing={2.1}>
              <DetailValue icon={ReceiptLongOutlined} label="Código de venta">
                {venta.codigo || '—'}
              </DetailValue>
              <DetailValue icon={PersonOutlineRounded} label="Cliente">
                {venta.cliente_nombre || 'Cliente sin nombre'}
              </DetailValue>
              <DetailValue icon={CalendarMonthOutlined} label="Fecha de registro">
                {formatoFecha(venta.fecha)}
              </DetailValue>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.25 }}>
              <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: 'info.main', color: 'info.contrastText', display: 'grid' }}>
                <PaymentsOutlined fontSize="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Cobro y entrega</Typography>
            </Stack>
            <Stack spacing={2.1}>
              <DetailValue icon={PaymentsOutlined} label="Método de pago">
                {readable(venta.metodo_pago)}
              </DetailValue>
              <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Estado de pago</Typography>
                <Chip size="small" label={readable(venta.estado_pago)} color={statusColor(venta.estado_pago, 'payment')} />
              </Stack>
              <Divider />
              <DetailValue icon={LocalShippingOutlined} label="Transportista">
                {venta.transportista_nombre || 'Sin transportista'}
              </DetailValue>
              <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Estado de envío</Typography>
                <Chip size="small" label={readable(venta.estado_envio)} color={statusColor(venta.estado_envio, 'shipping')} />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.25 }}>
              <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: 'success.main', color: 'success.contrastText', display: 'grid' }}>
                <TrendingUpRounded fontSize="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Totales y rentabilidad</Typography>
            </Stack>
            <Stack spacing={1.2}>
              <AmountLine label="Productos antes de descuento" value={formatMoney(totalSinDescuento)} />
              <AmountLine label="Descuentos aplicados" value={formatMoney(descuentoTotal)} />
              <AmountLine label="Costo de envío" value={formatMoney(costoEnvio)} />
              <AmountLine label="Comisión de transportista" value={formatMoney(comision)} />
              <Divider sx={{ my: 0.35 }} />
              <AmountLine label="Total a cobrar" value={formatMoney(totalNeto)} emphasis />
              <Box sx={{ mt: 0.6, p: 1.4, borderRadius: 2, bgcolor: gananciaNeta < 0 ? 'error.light' : 'success.light' }}>
                <AmountLine
                  label="Ganancia neta"
                  value={formatMoney(gananciaNeta)}
                  emphasis
                  negative={gananciaNeta < 0}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5, px: 0.5 }}>
        La ganancia neta descuenta el costo histórico de los productos y la comisión del transportista. El cargo de envío se muestra por separado.
      </Typography>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Inventory2Outlined color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Productos vendidos</Typography>
          </Stack>
          <Chip size="small" variant="outlined" label={`${lineas.length} ${lineas.length === 1 ? 'producto' : 'productos'}`} />
        </Stack>
        <TableContainer sx={{ borderTop: 1, borderColor: 'divider', maxWidth: '100%' }}>
          <Table size="small" stickyHeader aria-label="Productos incluidos en la venta" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>ID</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Precio unit.</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Antes de descuento</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Descuento</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">Ganancia</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {lineas.length > 0 ? (
                lineas.map((ln) => {
                  const totalBruto = Number(ln.cantidad || 0) * Number(ln.precio_unitario || 0);
                  const descuentoLinea =
                    ln.descuento_monto != null
                      ? Number(ln.descuento_monto || 0)
                      : totalBruto * (Number(ln.descuento || 0) / 100);
                  const gananciaLinea = Number(ln.ganancia_linea || 0);
                  return (
                    <TableRow key={ln.detalle_id} hover>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}>{ln.detalle_id}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 180 }}>
                          {ln.imagen_url ? (
                            <Box
                              component="img"
                              src={ln.imagen_url}
                              alt={ln.producto_nombre}
                              sx={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 1.5, bgcolor: 'action.hover', flex: '0 0 auto' }}
                            />
                          ) : (
                            <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: 'action.hover', color: 'text.secondary', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
                              <Inventory2Outlined fontSize="small" />
                            </Box>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {ln.producto_nombre || productosMap[ln.producto_id] || 'Producto sin nombre'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{ln.cantidad}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatMoney(ln.precio_unitario)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatMoney(totalBruto)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatMoney(descuentoLinea)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{formatMoney(ln.subtotal)}</TableCell>
                      <TableCell align="right">
                        <Typography component="span" variant="body2" sx={{ color: gananciaLinea < 0 ? 'error.main' : 'success.main', fontWeight: 800 }}>
                          {formatMoney(gananciaLinea)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    Sin líneas de detalle
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}
